import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, Users, Mail, FileText, Download } from "lucide-react";
import { api } from "../../lib/api";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="card !p-5" data-testid={testid}><div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon size={14} className="text-[var(--teal)]" /> {label}</div><div className="mt-2 font-display text-3xl font-bold text-[var(--navy)]">{value ?? "—"}</div></div>
);

const List = ({ title, items, total, testid }) => (
  <div className="card" data-testid={testid}>
    <h3 className="text-sm font-semibold text-[var(--navy)]">{title}</h3>
    <ul className="mt-3 space-y-2">
      {items.length === 0 && <li className="text-xs text-slate-400">No data yet</li>}
      {items.map((it) => (
        <li key={it.name} className="text-xs">
          <div className="flex justify-between gap-3 text-slate-700"><span className="truncate">{it.name}</span><span className="font-mono text-slate-500">{it.count}</span></div>
          <div className="mt-1 h-1 rounded bg-slate-100"><div className="h-1 rounded bg-[var(--teal)]" style={{ width: `${total ? Math.max(2, (it.count / total) * 100) : 0}%` }} /></div>
        </li>
      ))}
    </ul>
  </div>
);

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics", { params: { days } }).then((r) => setD(r.data)); }, [days]);

  const fmt = (s) => new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><div className="eyebrow">Audience</div><h1 className="mt-1 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Website analytics</h1><p className="mt-1 text-sm text-slate-500">Privacy-friendly, first-party tracking — no cookies, no third parties. Admin pages are excluded.</p></div>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">{[7, 30, 90].map((n) => <button key={n} data-testid={`analytics-range-${n}`} onClick={() => setDays(n)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${days === n ? "bg-[var(--navy)] text-white" : "text-slate-600"}`}>{n} days</button>)}</div>
      </div>
      {!d ? <div className="mt-8 text-slate-500">Loading…</div> : (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi icon={Eye} label="Page views" value={d.pageviews} testid="kpi-pageviews" />
            <Kpi icon={Users} label="Unique visitors" value={d.visitors} testid="kpi-visitors" />
            <Kpi icon={Mail} label="Contact enquiries" value={d.conversions.contact_submit} testid="kpi-contact" />
            <Kpi icon={FileText} label="Job applications" value={d.conversions.cv_apply} testid="kpi-cv" />
            <Kpi icon={Download} label="PDF downloads" value={d.conversions.pdf_download} testid="kpi-downloads" />
          </div>
          <div className="card mt-4" data-testid="analytics-chart">
            <h3 className="text-sm font-semibold text-[var(--navy)]">Daily views &amp; visitors</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.series} margin={{ left: -20, right: 8 }}>
                  <defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#0078a1" stopOpacity="0.35" /><stop offset="1" stopColor="#0078a1" stopOpacity="0" /></linearGradient></defs>
                  <CartesianGrid stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={fmt} tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={fmt} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#0078a1" fill="url(#gv)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#14b8a6" fill="none" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <List title="Top pages" items={d.pages} total={d.pageviews} testid="analytics-pages" />
            <List title="Traffic sources" items={d.sources} total={d.pageviews} testid="analytics-sources" />
            <div className="grid gap-4">
              <List title="Countries" items={d.countries} total={d.pageviews} testid="analytics-countries" />
              <div className="card" data-testid="analytics-devices">
                <h3 className="text-sm font-semibold text-[var(--navy)]">Devices</h3>
                <div className="mt-3 h-24"><ResponsiveContainer width="100%" height="100%"><BarChart data={d.devices} layout="vertical" margin={{ left: 0 }}><XAxis type="number" hide /><YAxis type="category" dataKey="name" width={64} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#0078a1" radius={4} /></BarChart></ResponsiveContainer></div>
              </div>
            </div>
          </div>
          <div className="mt-4"><List title="Most downloaded PDFs" items={d.downloads} total={d.conversions.pdf_download} testid="analytics-downloads" /></div>
        </>
      )}
    </div>
  );
}
